// Header for the family workspace screen: photo (upload/replace), inline
// name edit, category breadcrumb (edit via CategoryModal), variant count
// badge, a "Merge into…" button opening MergeFamilyModal with this family as
// the source, and a back link to the master products list.

import { useUploadFileApi } from 'apis/config/use-upload-file-api';
import { MasterProductFamily, useUpdateMasterProductFamilyApi } from 'apis/master-product-families';
import { useGetDealCategoryTreeApi } from 'apis/supplier-prices';
import { CategoryModal } from 'components/category-picker/category-modal';
import { findCategoryPath } from 'components/category-picker/category-tree-helpers';
import type { CategoryRef } from 'components/category-picker/types';
import { Badge, Box, Button, Flex, HStack, Image, Input, Spinner, Text, useToast } from 'design-system';
import { MergeFamilyModal } from 'pages/inquiry-processor/components/family-workspace/merge-family-modal';
import React, { useEffect, useRef, useState } from 'react';
import { FiArrowLeft, FiChevronDown, FiImage } from 'react-icons/fi';

export interface FamilyHeaderProps {
  family: MasterProductFamily;
  variantCount?: number;
  onBack: () => void;
}

export const FamilyHeader = ({ family, variantCount, onBack }: FamilyHeaderProps) => {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: categoryTreeData } = useGetDealCategoryTreeApi();
  const categoryTree = categoryTreeData?.data ?? [];
  const categoryPath = findCategoryPath(categoryTree, family.catalogCategoryId);
  const categoryLabel = categoryPath.length
    ? categoryPath.map((n) => n.title || `Untitled (${n.id})`).join(' › ')
    : `#${family.catalogCategoryId}`;

  const updateFamily = useUpdateMasterProductFamilyApi({
    onError: (error: any) => {
      const status = error?.response?.status;
      const message =
        status === 409
          ? 'A family with this name already exists.'
          : error?.response?.data?.message || 'Failed to update family. Please try again.';
      toast({ title: message, status: 'error' });
    },
  });

  const upload = useUploadFileApi({
    onSuccess: (data: any) => {
      const url = data?.url || data?.fileUrl || data?.path || '';
      if (!url) return;
      updateFamily.mutate({ id: family.id, data: { imageUrl: url } });
    },
  });

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    upload.mutate({ body });
    // Allow re-selecting the same file consecutively.
    e.target.value = '';
  };

  // Inline name edit — Text becomes an Input on click; Enter/blur saves,
  // Escape cancels. A ref (not state) suppresses the blur-triggered save
  // that would otherwise immediately follow an Escape-driven unmount.
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(family.universalName);
  const suppressBlurRef = useRef(false);

  useEffect(() => {
    setNameDraft(family.universalName);
  }, [family.universalName]);

  const startEditName = () => {
    setNameDraft(family.universalName);
    setIsEditingName(true);
  };

  const commitName = () => {
    if (suppressBlurRef.current) {
      suppressBlurRef.current = false;
      return;
    }
    setIsEditingName(false);
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === family.universalName) {
      setNameDraft(family.universalName);
      return;
    }
    updateFamily.mutate({ id: family.id, data: { universalName: trimmed } });
  };

  const cancelEditName = () => {
    suppressBlurRef.current = true;
    setNameDraft(family.universalName);
    setIsEditingName(false);
  };

  const onCategorySelect = (category: CategoryRef) => {
    if (category.id === family.catalogCategoryId) return;
    updateFamily.mutate({ id: family.id, data: { catalogCategoryId: category.id } });
  };

  const [isMergeOpen, setIsMergeOpen] = useState(false);

  return (
    <Box mb='4'>
      <Button leftIcon={<FiArrowLeft />} variant='ghost' size='sm' mb='3' onClick={onBack}>
        Back
      </Button>

      <Flex align='center' gap='4' wrap='wrap'>
        <Box position='relative' boxSize='64px' flexShrink={0}>
          {family.imageUrl ? (
            <Image src={family.imageUrl} alt={family.universalName} boxSize='64px' objectFit='cover' rounded='md' />
          ) : (
            <Flex
              boxSize='64px'
              bg='gray.100'
              rounded='md'
              align='center'
              justify='center'
              color='gray.300'
              fontSize='24px'>
              <FiImage />
            </Flex>
          )}
          <input ref={fileRef} type='file' accept='image/*' hidden onChange={onFile} />
          <Button
            size='xs'
            variant='outline'
            position='absolute'
            bottom='-10px'
            left='0'
            right='0'
            mx='auto'
            bg='white'
            isLoading={upload.isLoading}
            onClick={() => fileRef.current?.click()}>
            {family.imageUrl ? 'Replace' : 'Upload'}
          </Button>
        </Box>

        <Box flex='1' minW='240px'>
          {isEditingName ? (
            <Input
              autoFocus
              size='lg'
              fontWeight='bold'
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                } else if (e.key === 'Escape') {
                  cancelEditName();
                }
              }}
            />
          ) : (
            <Text
              fontSize='2xl'
              fontWeight='bold'
              cursor='pointer'
              onClick={startEditName}
              _hover={{ color: 'red.500' }}>
              {family.universalName}
            </Text>
          )}

          <HStack mt='1' spacing='2'>
            <CategoryModal
              value={{ id: family.catalogCategoryId, title: categoryLabel }}
              onSelect={onCategorySelect}
              trigger={
                <Button variant='link' size='sm' fontWeight='normal' color='gray.600' rightIcon={<FiChevronDown />}>
                  {categoryLabel}
                </Button>
              }
            />
            {family.isActive === false && <Badge colorScheme='orange'>Hidden from Marketplace</Badge>}
            {variantCount != null && (
              <Badge colorScheme='gray'>
                {variantCount} variant{variantCount === 1 ? '' : 's'}
              </Badge>
            )}
            {updateFamily.isLoading && <Spinner size='xs' color='gray.400' />}
          </HStack>
        </Box>

        <Button size='sm' variant='outline' onClick={() => setIsMergeOpen(true)}>
          Merge into…
        </Button>
      </Flex>

      <MergeFamilyModal
        isOpen={isMergeOpen}
        onClose={() => setIsMergeOpen(false)}
        sourceFamily={{ id: family.id, universalName: family.universalName }}
      />
    </Box>
  );
};
